import { subjectKind } from "../assistant/api";
import { linksHostHash, portGuardHostHash } from "../portguard/edgeHosts";

// One formatter per idea, shared by every SOC surface.
//
// These all started life as inline expressions next to the thing they rendered,
// which is how the console ended up telling an operator two different things
// about the same number: the window selector printed "10080m" while every
// notice on the same page called that window "7d". A second copy of a formatter
// is a drift waiting to happen, so each one lives here exactly once.

// The window selector's own vocabulary — "5m", "60m", "24h".
export function rangeLabel(rangeMin: number): string {
  // Days once past a day. The old form returned "24h" for ANY range >= 1440,
  // so a 7-day window would have been labelled 24h everywhere the label is
  // used — including the notices that state which window a number covers.
  if (rangeMin >= 1440) {
    const days = Math.round(rangeMin / 1440);
    return days === 1 ? "24h" : `${days}d`;
  }
  if (rangeMin >= 60) return `${Math.round(rangeMin / 60)}h`;
  return `${rangeMin}m`;
}

// A span in plain words: "45s", "3m", "1h 12m".
export function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.round(totalSec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const rem = min % 60;
  return rem ? `${hr}h ${rem}m` : `${hr}h`;
}

export function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "n/a";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/**
 * The queue's clock: absolute, to the minute, tabular.
 *
 * Deliberately NOT `relTime`. A column of "2m / 3h / just now" cannot be
 * scanned down — every row's meaning depends on when you read it, and two rows
 * both saying "3h" may be an hour apart. The board's Alerts queue prints an
 * absolute time for exactly that reason, and the console only ever had it in a
 * `title=` tooltip.
 */
/**
 * BUILT ONCE. `toLocaleTimeString` with options constructs a fresh
 * Intl.DateTimeFormat on every call, and this one is called per row per
 * render — on a queue of any size, in a test environment that has to load ICU
 * data, that is the difference between a page and a stall.
 *
 * 24-hour, and `en-GB` rather than the browser's locale: the board prints
 * "11:42"; a US locale prints "11:42 AM", which is four more characters in a
 * 60px column and ambiguous in an estate whose hosts are in three time zones.
 */
const CLOCK = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });

export function clockTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "n/a";
  return CLOCK.format(date);
}

const DAY_MONTH = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });

/**
 * Whether a window crosses midnight, so that a clock alone cannot place a time
 * in it: "08:47" in a 7-day window is any of seven mornings.
 */
export function windowSpansDays(startMs: number, endMs: number): boolean {
  return Number.isFinite(startMs) && Number.isFinite(endMs) && new Date(startMs).toDateString() !== new Date(endMs).toDateString();
}

/**
 * A clock time that names its day when the reading's window spans days
 * ("17 Oct 08:47"), and is the bare clock ("08:47") in a window of one day —
 * the inspector's convention (EntityInspector stampWords), so the readings
 * beside it say the day the same way (close10: readings name their period).
 */
export function clockInWindow(value: string, dated: boolean, seconds = false): string {
  const clock = seconds ? clockSeconds(value) : clockTime(value);
  return dated && clock !== "n/a" ? `${DAY_MONTH.format(new Date(value))} ${clock}` : clock;
}

const DAY = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric" });
const CLOCK_SECONDS = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

/** The queue's clock to the second — what an inspector prints beside a row that reads "20:48". */
export function clockSeconds(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "n/a" : CLOCK_SECONDS.format(date);
}

/**
 * Which day a clock time belongs to — the line under it in the queue.
 *
 * A 7-day window prints "18:14" for yesterday and for today alike, so the
 * queue's order looked broken where it had crossed midnight. Absolute, for the
 * same reason the clock is: "today" / "yesterday" / "Tue 22" mean the same
 * thing whenever they are read.
 */
export function dayLabel(value: string, now = Date.now()): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const startOf = (ms: number) => new Date(ms).setHours(0, 0, 0, 0);
  const days = Math.round((startOf(now) - startOf(date.getTime())) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return DAY.format(date);
}

export function shortId(value: string) {
  return value.length > 18 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value;
}

/**
 * Whether a value is a Tetragon exec id — base64 of "node:ktime:pid" — rather
 * than a path or a name. Decoded and matched, not guessed from its length, so a
 * binary that merely looks like base64 is still a binary.
 */
export function isExecId(value?: string): boolean {
  if (!value || value.includes("/") || value.length < 16 || !/^[A-Za-z0-9+=]+$/.test(value)) return false;
  try {
    return /^[^:\s]+:\d+:\d+$/.test(atob(value));
  } catch {
    return false;
  }
}

/** The pid inside a Tetragon exec id ("node:ktime:pid"), or undefined. */
export function execIdPid(value?: string): string | undefined {
  if (!isExecId(value)) return undefined;
  try {
    return atob(value as string).split(":").pop();
  } catch {
    return undefined;
  }
}

/**
 * What a person reads a decision by: its program; failing that, the pid inside
 * its exec id; never the base64 token itself (Jeff's Containment card,
 * 2026-09-24, listed "throttle  OGIwZjkxYWRiZjU4OjQ5…" five times).
 */
/**
 * A decision's action in words. The ledger's own spelling reached the screen —
 * "vpn_absolute_timeout" in a 70px column, overprinting the subject beside it
 * (Jeff's dashboard, 2026-09-24). The VPN audit timeline already said
 * "absolute timeout"; every surface now says the same thing the same way.
 */
export function actionWords(action?: string): string {
  const raw = (action || "").trim();
  if (!raw) return "unnamed";
  if (raw.startsWith("vpn_")) return `VPN ${raw.slice(4).replace(/_/g, " ")}`;
  return raw.replace(/_/g, " ");
}

/**
 * An alert or event whose subject is NOT a process: a network device
 * ("device:<mac>"), a VPN device ("vpn:<peer id>"), the VPN plane
 * ("vpn:plane"), a link ("link:<interface>", BW-04) or the playbook plane or
 * one playbook ("playbook:plane", "playbook:<name>", §7). Network findings,
 * link findings, VPN audit alerts and the playbooks' own alerts carry these,
 * and the alert drill must not read one as an exec id — there is no process
 * chain to fetch and no process ladder to press. Null for a process subject.
 */
export type NetworkSubject = {
  kind:
    | "device"
    | "device-plane"
    | "vpn-peer"
    | "vpn-plane"
    | "link"
    | "playbook"
    | "certificate"
    | "edge"
    | "firewall"
    | "asset"
    | "web-client"
    | "web-plane"
    | "flood"
    // Any other subject that is not a process (the graph-guard review): the
    // engine's own ledger, integrity, restart and sensor findings, a policy
    // change, a kind this console does not read yet.
    | "other";
  /** The MAC, the peer id, "plane", the interface, or the playbook's name. */
  id: string;
  /** How a person says it. */
  label: string;
  /** Where it is inspected and, if at all, contained. */
  href: string;
  /**
   * The assistant surface that reads it. A link and a playbook have no
   * surface of their own yet (their boards decide one), so the alert drill's.
   */
  surface: "devices" | "vpn" | "alert-drill";
  /**
   * False when no page of its own reads it (a policy change, a kind not read
   * yet): href is then the alert queue it was raised to, and a drill draws no
   * "Open …" for it (the graph-guard re-verifier). Absent when it has a page.
   */
  page?: false;
};

export function networkSubject(execId?: string, host?: string): NetworkSubject | null {
  const s = execId?.trim();
  if (!s) return null;
  // g5 (board 23, S-1): the device plane's own alerts (its rules armed,
  // disarmed or reverted, DEV-02's restart summary) are about the plane, not a
  // device called "plane". Its page is Devices › Rules.
  if (s === "device:plane") {
    return { kind: "device-plane", id: "plane", label: "the device plane", href: "/devices#rules", surface: "devices" };
  }
  if (s.startsWith("device:")) {
    const mac = s.slice("device:".length);
    return { kind: "device", id: mac, label: `device ${mac}`, href: `/devices#device=${encodeURIComponent(mac)}`, surface: "devices" };
  }
  if (s.startsWith("vpn:")) {
    const id = s.slice("vpn:".length);
    if (/^\d+$/.test(id)) return { kind: "vpn-peer", id, label: `VPN device #${id}`, href: `/vpn#peer=${id}`, surface: "vpn" };
    return { kind: "vpn-plane", id, label: "the VPN plane", href: "/vpn", surface: "vpn" };
  }
  // §7: a certificate (cert:<name>), the edge plane (edge:plane), the firewall
  // (firewall:plane, firewall:<from>-<to>) and an exposure asset (asset:<id>)
  // are subjects of their own, never processes. Their addresses are the
  // reserved /#web, /#firewall and /#exposure (their boards may refine them).
  // §7 (WAF-03, WEB-01, FLOOD-07): a web client (web:<client key>, an IPv4
  // address or an IPv6 /64), the web plane (web:plane) and the flood guard
  // (flood:plane) are never processes. The web's address is the reserved
  // /#web (s45-console refines a client's drill). The flood guard's board
  // (docs/design-v1/sys/Flood.*) places it as a section of Devices › Traffic,
  // opened in place by /devices#traffic/flood (s57-console).
  if (s === "web:plane") {
    return { kind: "web-plane", id: "plane", label: "the web plane", href: "/#web", surface: "alert-drill" };
  }
  if (s.startsWith("web:")) {
    const id = s.slice("web:".length);
    // The client's own drill, by the address a note on it opens (features/notes
    // noteSubjectHref): Respond › Web reads ?client= and opens Clients on it.
    // The key is an address or an IPv6 /64, so it is encoded.
    const href = id ? `/?client=${encodeURIComponent(id)}#web` : "/#web";
    return { kind: "web-client", id, label: `web client ${id}`, href, surface: "alert-drill" };
  }
  if (s.startsWith("flood:")) {
    const id = s.slice("flood:".length);
    return { kind: "flood", id, label: id === "plane" ? "the flood guard" : `flood guard ${id}`, href: "/devices#traffic/flood", surface: "alert-drill" };
  }
  if (s.startsWith("cert:")) {
    const id = s.slice("cert:".length);
    return { kind: "certificate", id, label: `the certificate for ${id}`, href: "/#web", surface: "alert-drill" };
  }
  if (s.startsWith("edge:")) {
    const id = s.slice("edge:".length);
    return { kind: "edge", id, label: id === "plane" ? "the edge plane" : `edge ${id}`, href: "/#web", surface: "alert-drill" };
  }
  if (s.startsWith("firewall:")) {
    const id = s.slice("firewall:".length);
    const label = id === "plane" ? "the firewall plane" : `firewall zone pair ${id}`;
    return { kind: "firewall", id, label, href: "/#firewall", surface: "alert-drill" };
  }
  if (s.startsWith("asset:")) {
    const id = s.slice("asset:".length);
    return { kind: "asset", id, label: `exposure asset #${id}`, href: "/#exposure", surface: "alert-drill" };
  }
  // BW-04: a link is read, never contained. Where its readings live waits for
  // BW-03's board (rule E13; 0036's draft default is a section of Devices), so
  // it opens the Devices page, which holds the device plane on its uplink.
  if (s.startsWith("link:")) {
    const id = s.slice("link:".length);
    // g5 (board 23): the Traffic lens of Devices holds it (g1, board 22).
    return { kind: "link", id, label: `link ${id}`, href: `/devices#traffic?iface=${encodeURIComponent(id)}`, surface: "alert-drill" };
  }
  // §7: the playbooks are policy documents; their surface waits for its board,
  // so their alerts open the Policies surface.
  if (s.startsWith("playbook:")) {
    const id = s.slice("playbook:".length);
    const label = id === "plane" ? "the playbook plane" : `playbook ${id}`;
    // g5 (board 23): the Playbooks surface's own addresses (g3, model.ts).
    const href = id === "plane" ? "/#playbooks/safety" : `/#playbooks?name=${encodeURIComponent(id)}`;
    return { kind: "playbook", id, label, href, surface: "alert-drill" };
  }
  // THE EDGE APPLIANCE (API contract 2026-10-22: "the alert's subject is the
  // sensor itself"). The engine files Port guard's alerts under
  // sensor:port-guard (one device's as sensor:port-guard:<port>:<ids>) and the
  // internet links' under sensor:internet-links (one link's as
  // sensor:internet-link:<name>). Port guard is read in its own lens of
  // Devices (the owner's decision of 2026-10-22: the fourth lens, after
  // Traffic; Sensor Health keeps one line for it); the internet links in their
  // section of Devices › Traffic, never on Sensor Health, which has none.
  // On a control plane the alert's host (its agent) is chosen on the page it opens.
  const edge = edgeApplianceSubject(s, host);
  if (edge) return edge.subject;
  // THE SAME RULE AS THE SERVERS AND THE GRAPH (the graph-guard review,
  // 2026-10-15): whatever is not a process exec id's own form (subjectKind) is
  // no process either. This list of kinds let the engine's own "ledger:<host>",
  // "integrity:<host>" and "restart:…" alerts through, so the alert drill
  // offered them the jail form that both servers now refuse. Its own page is
  // not known here, so it is read where it was raised: the alert queue.
  if (subjectKind(s) !== "process") {
    const cut = s.indexOf(":");
    const kind = cut > 0 ? s.slice(0, cut) : "";
    const id = cut > 0 ? s.slice(cut + 1) : s;
    // An SSH client (the SSH server's log, failed logins per address: the T1110 detection) is named as one.
    const label = kind === "ssh" ? `SSH client ${id}` : kind ? `${kind} ${id}` : s;
    // An own key only: a plain lookup handed "constructor:x" Object's constructor as its page.
    const page = Object.hasOwn(OTHER_SUBJECT_PAGE, kind) ? OTHER_SUBJECT_PAGE[kind] : undefined;
    return page
      ? { kind: "other", id, label, href: page, surface: "alert-drill" }
      : { kind: "other", id, label, href: "/#alerts", surface: "alert-drill", page: false };
  }
  return null;
}

const PORT_GUARD_SUBJECT = "sensor:port-guard";
const INTERNET_LINKS_SUBJECT = "sensor:internet-links";
const INTERNET_LINK_PREFIX = "sensor:internet-link:";

/**
 * The edge appliance's two subjects (Port guard, the internet links), or null
 * for any other. On a control plane the alert names its host (its agent): the
 * page then opens with THAT host chosen (#portguard?host=…,
 * #traffic/links?host=…; the multi-tenant amendments). The engine's own
 * alerts are its own sensor's, so its addresses name no host.
 */
export function edgeApplianceSubject(s: string, host?: string): { edge: "port-guard" | "internet-links"; subject: NetworkSubject } | null {
  const agent = host?.trim() || "";
  if (s === INTERNET_LINKS_SUBJECT || s.startsWith(INTERNET_LINK_PREFIX)) {
    const name = s.startsWith(INTERNET_LINK_PREFIX) ? s.slice(INTERNET_LINK_PREFIX.length) : "";
    return {
      edge: "internet-links",
      subject: {
        kind: "other",
        id: name || "internet-links",
        label: name ? `internet link ${name}` : "the internet links",
        href: agent ? `/devices${linksHostHash(agent)}` : "/devices#traffic",
        surface: "alert-drill"
      }
    };
  }
  if (s === PORT_GUARD_SUBJECT || s.startsWith(`${PORT_GUARD_SUBJECT}:`)) {
    const rest = s.slice(PORT_GUARD_SUBJECT.length + 1);
    const cut = rest.indexOf(":");
    const port = cut >= 0 ? rest.slice(0, cut) : rest;
    const ids = cut >= 0 ? rest.slice(cut + 1) : "";
    return {
      edge: "port-guard",
      subject: {
        kind: "other",
        id: rest || "port-guard",
        label: port ? `the USB device on port ${port}${ids ? ` (${ids})` : ""}` : "Port guard",
        href: agent ? `/devices${portGuardHostHash(agent)}` : "/devices#portguard",
        surface: "alert-drill"
      }
    };
  }
  return null;
}

/**
 * Where the engine's own findings are read (the graph-guard verifiers): the
 * evidence ledger's anchors and checks, the integrity sealer and evidence loss
 * on Sensor Health; a containment found orphaned at restart on Processes. A
 * kind with no page here (a policy change, a kind not read yet) stays in the
 * alert queue it was raised to.
 */
const OTHER_SUBJECT_PAGE: Readonly<Record<string, string>> = {
  ledger: "/#kprobes",
  integrity: "/#kprobes",
  sensor: "/#kprobes",
  restart: "/choke"
};

export function decisionSubject(decision: { binary?: string; target?: string }): string {
  // A VPN decision's subject is a PEER ("vpn:<id>") or the plane itself, not a
  // process; its raw address read as a binary called "vpn:7".
  const vpn = decision.target?.match(/^vpn:(.+)$/);
  if (vpn) return /^\d+$/.test(vpn[1]) ? `peer ${vpn[1]}` : vpn[1] === "plane" ? "the VPN plane" : vpn[1];
  if (decision.binary) return baseName(decision.binary);
  const pid = execIdPid(decision.target);
  if (pid) return `pid ${pid}`;
  return decision.target ? baseName(decision.target) : "target not reported";
}

export function baseName(path?: string): string {
  if (!path) return "process";
  const idx = path.lastIndexOf("/");
  return idx >= 0 ? path.slice(idx + 1) || path : path;
}

export function shortGraphLabel(value: string, max = 24) {
  const clean = value.replace(/\s+/g, " ").trim();
  if (!clean) return "unknown";
  if (clean.length <= max) return clean;
  const leaf = clean.split("/").filter(Boolean).at(-1);
  const candidate = leaf && leaf.length <= Math.max(10, max) ? leaf : clean;
  if (candidate.length <= max) return candidate;
  const side = Math.max(5, Math.floor((max - 3) / 2));
  return `${candidate.slice(0, side)}...${candidate.slice(-side)}`;
}
